import { v4 as uuidv4 } from 'uuid';
import {
  ActionIcon,
  Button,
  Paper,
  Radio,
  Text,
  TextInput,
} from '@mantine/core';
import styles from './ContactFieldCollection.module.css';
import { EmailAddress, PhoneNumber } from '@/database/baseTypes';
import { PlusIcon, Trash2Icon } from 'lucide-react';

export interface ContactFieldCollectionData<
  T extends { id: string; value: string },
> {
  items: T[];
  primaryItemId: string | null;
}

export const ContactFieldCollection = <
  T extends { id: string; value: string },
>({
  collectionName,
  itemDescriptor,
  textInputLeftSection,
  data,
  onChange,
}: {
  collectionName: string;
  itemDescriptor: string;
  textInputLeftSection: React.ReactNode;
  data: ContactFieldCollectionData<T>;
  onChange: (data: ContactFieldCollectionData<T>) => void;
}) => {
  const handleDelete = (deletedItem: T) => {
    onChange({
      items: data.items.filter((i) => i.id !== deletedItem.id),
      primaryItemId:
        data.primaryItemId === deletedItem.id
          ? (data.items.find((i) => i.id !== deletedItem.id)?.id ?? null)
          : data.primaryItemId,
    });
  };

  const handleAdd = () => {
    const newItem = { id: uuidv4(), value: '' } as T;
    const nextPrimaryId =
      data.items.length === 0 ? newItem.id : data.primaryItemId;

    onChange({
      items: [...data.items, newItem],
      primaryItemId: nextPrimaryId,
    });
  };

  const handleValueChange = (newValue: string, changedItem: T) => {
    onChange({
      items: data.items.map((i) =>
        i.id === changedItem.id ? { ...i, value: newValue } : i,
      ),
      primaryItemId: data.primaryItemId,
    });
  };

  const handlePrimaryChange = (newPrimaryItem: T) => {
    onChange({
      items: data.items,
      primaryItemId: newPrimaryItem.id,
    });
  };

  return (
    <Paper className={styles.collectionContainer}>
      <CollectionHeader
        collectionName={collectionName}
        collectionLength={data.items.length}
      />
      <ul className={styles.collectionList}>
        {data.items.map((item, itemIndex) => (
          <CollectionRow
            key={item.id}
            item={item}
            label={`${itemDescriptor} ${itemIndex + 1}`}
            onValueChange={(e) => {
              handleValueChange(e.target.value, item);
            }}
            isPrimary={data.primaryItemId === item.id}
            canDelete={true}
            textInputLeftSection={textInputLeftSection}
            onPrimaryChange={() => handlePrimaryChange(item)}
            onDelete={() => handleDelete(item)}
          />
        ))}
      </ul>
      <AddToCollectionButton
        title={`Add ${itemDescriptor}`}
        onClick={handleAdd}
      />
    </Paper>
  );
};

const CollectionHeader = ({
  collectionName,
  collectionLength,
}: {
  collectionName: string;
  collectionLength: number;
}) => {
  return (
    <div className={styles.collectionGridLine}>
      <Text size='sm' fw={500}>
        {collectionName}
      </Text>
      <Text
        size='xs'
        style={{
          justifySelf: 'center',
          alignSelf: 'flex-end',
        }}
      >
        {collectionLength > 0 && 'Primary'}
      </Text>
      <Text size='xs'></Text>
    </div>
  );
};

const CollectionRow = ({
  item,
  label,
  isPrimary,
  canDelete,
  textInputLeftSection,
  onValueChange,
  onPrimaryChange,
  onDelete,
}: {
  item: PhoneNumber | EmailAddress;
  label: string;
  isPrimary: boolean;
  canDelete: boolean;
  textInputLeftSection: React.ReactNode;
  onValueChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onPrimaryChange: () => void;
  onDelete: () => void;
}) => {
  return (
    <li className={styles.collectionGridLine} key={item.id}>
      <TextInput
        aria-label={label}
        leftSection={textInputLeftSection}
        key={`${item.id}-input`}
        value={item.value}
        onChange={onValueChange}
        autoComplete='off'
        flex={1}
      />

      <Radio
        aria-label={`Set ${label} as primary`}
        classNames={{
          root: styles.primaryRadio,
        }}
        style={{ justifySelf: 'center' }}
        checked={isPrimary}
        onChange={onPrimaryChange}
        color='blue.4'
      />

      {canDelete && (
        <ActionIcon
          aria-label={`Delete ${label}`}
          variant='subtle'
          onClick={onDelete}
        >
          <Trash2Icon size={16} />
        </ActionIcon>
      )}
    </li>
  );
};

const AddToCollectionButton = ({
  title,
  onClick,
}: {
  title: string;
  onClick: () => void;
}) => {
  return (
    <Button
      variant='subtle'
      leftSection={<PlusIcon size={16} />}
      className={styles.addToCollectionButton}
      onClick={onClick}
    >
      {title}
    </Button>
  );
};
